<template>
    <transition name="mobile-editor">
        <div
            v-if="visible"
            class="mobile-field-editor"
        >
            <!-- Sticky Header -->
            <div class="editor-header">
                <button
                    class="back-btn"
                    @click="handleBack"
                    aria-label="Go back"
                >
                    <i class="fa fa-arrow-left"></i>
                </button>
                <h3 class="editor-title">{{ fieldLabel }}</h3>
                <button
                    v-if="fieldType === 'signature'"
                    class="clear-btn"
                    @click="clearSignature"
                    :disabled="!hasSignature"
                >
                    Clear
                </button>
                <div v-else class="header-spacer"></div>
            </div>

            <!-- Progress Indicator (hidden in landscape for signatures) -->
            <div class="step-indicator" v-if="totalSteps > 1 && !(isLandscape && fieldType === 'signature')">
                <span class="step-text">STEP {{ currentStep }} of {{ totalSteps }}</span>
                <div class="step-dots">
                    <span
                        v-for="step in totalSteps"
                        :key="step"
                        class="step-dot"
                        :class="{
                            'completed': step < currentStep,
                            'current': step === currentStep,
                            'pending': step > currentStep
                        }"
                    ></span>
                </div>
            </div>

            <!-- Field Content -->
            <div class="editor-content">
                <!-- Landscape Signature Layout -->
                <div v-if="fieldType === 'signature' && isLandscape" class="signature-editor landscape">
                    <div class="canvas-wrapper" ref="canvasWrapper">
                        <VueSignaturePad
                            v-if="!readonly"
                            :width="canvasWidth + 'px'"
                            :height="canvasHeight + 'px'"
                            :options="signaturePadOptions"
                            ref="signaturePad"
                        />
                        <div v-if="readonly" class="readonly-signature">
                            <i class="fa fa-lock"></i>
                            <p>This signature is assigned to another role</p>
                        </div>
                    </div>
                    <div class="landscape-actions">
                        <button
                            class="btn-primary-action"
                            @click="saveField"
                            :disabled="saving || !canSave"
                        >
                            <i class="fa fa-check" v-if="!saving"></i>
                            <i class="fa fa-spinner fa-spin" v-else></i>
                            Save Signature
                        </button>
                        <button
                            v-if="canSkip"
                            class="btn-secondary-action"
                            @click="skipField"
                        >
                            Skip
                        </button>
                    </div>
                </div>

                <!-- Portrait Signature Layout -->
                <div v-else-if="fieldType === 'signature'" class="signature-editor">
                    <div class="canvas-wrapper" ref="canvasWrapper">
                        <VueSignaturePad
                            v-if="!readonly"
                            :width="canvasWidth + 'px'"
                            :height="canvasHeight + 'px'"
                            :options="signaturePadOptions"
                            ref="signaturePad"
                        />
                        <div v-if="readonly" class="readonly-signature">
                            <i class="fa fa-lock"></i>
                            <p>This signature is assigned to another role</p>
                        </div>
                    </div>
                    <p v-if="!readonly && !hasSignature" class="draw-hint">
                        Draw your signature above
                    </p>
                </div>

                <!-- Text Field -->
                <div v-else-if="fieldType === 'text'" class="text-editor">
                    <label :for="fieldId">{{ fieldLabel }}</label>
                    <input
                        :id="fieldId"
                        type="text"
                        v-model="fieldValue"
                        class="mobile-input"
                        :placeholder="placeholder"
                        :required="isRequired"
                        :disabled="readonly"
                        ref="textInput"
                    />
                </div>

                <!-- Textarea Field -->
                <div v-else-if="fieldType === 'textarea'" class="textarea-editor">
                    <label :for="fieldId">{{ fieldLabel }}</label>
                    <textarea
                        :id="fieldId"
                        v-model="fieldValue"
                        class="mobile-textarea"
                        :placeholder="placeholder"
                        :required="isRequired"
                        :disabled="readonly"
                        rows="5"
                        ref="textareaInput"
                    ></textarea>
                </div>

                <!-- Email Field -->
                <div v-else-if="fieldType === 'email'" class="email-editor">
                    <label :for="fieldId">{{ fieldLabel }}</label>
                    <input
                        :id="fieldId"
                        type="email"
                        v-model="fieldValue"
                        class="mobile-input"
                        :placeholder="placeholder || 'email@example.com'"
                        :required="isRequired"
                        :disabled="readonly"
                        inputmode="email"
                        ref="emailInput"
                    />
                </div>

                <!-- Phone Field -->
                <div v-else-if="fieldType === 'phone'" class="phone-editor">
                    <label :for="fieldId">{{ fieldLabel }}</label>
                    <input
                        :id="fieldId"
                        type="tel"
                        v-model="fieldValue"
                        class="mobile-input"
                        :placeholder="placeholder || '(555) 555-5555'"
                        :required="isRequired"
                        :disabled="readonly"
                        inputmode="tel"
                        ref="phoneInput"
                    />
                </div>

                <!-- Number Field -->
                <div v-else-if="fieldType === 'number'" class="number-editor">
                    <label :for="fieldId">{{ fieldLabel }}</label>
                    <input
                        :id="fieldId"
                        type="number"
                        v-model="fieldValue"
                        class="mobile-input"
                        :placeholder="placeholder"
                        :required="isRequired"
                        :disabled="readonly"
                        inputmode="numeric"
                        ref="numberInput"
                    />
                </div>

                <!-- Date Field -->
                <div v-else-if="fieldType === 'date'" class="date-editor">
                    <label :for="fieldId">{{ fieldLabel }}</label>
                    <input
                        :id="fieldId"
                        type="date"
                        v-model="fieldValue"
                        class="mobile-input"
                        :required="isRequired"
                        :disabled="readonly"
                        ref="dateInput"
                    />
                </div>

                <!-- Select/Dropdown Field -->
                <div v-else-if="fieldType === 'select' || fieldType === 'dropdown'" class="select-editor">
                    <label :for="fieldId">{{ fieldLabel }}</label>
                    <select
                        :id="fieldId"
                        v-model="fieldValue"
                        class="mobile-select"
                        :required="isRequired"
                        :disabled="readonly"
                        ref="selectInput"
                    >
                        <option value="">-- Select an option --</option>
                        <option
                            v-for="option in fieldOptions"
                            :key="option.value || option"
                            :value="option.value || option"
                        >
                            {{ option.label || option }}
                        </option>
                    </select>
                </div>

                <!-- Checkbox Field -->
                <div v-else-if="fieldType === 'checkbox'" class="checkbox-editor">
                    <label class="mobile-checkbox">
                        <input
                            type="checkbox"
                            v-model="fieldValue"
                            :required="isRequired"
                            :disabled="readonly"
                        />
                        <span class="checkbox-box"></span>
                        <span class="checkbox-text">{{ fieldLabel }}</span>
                    </label>
                </div>

                <!-- Radio Field -->
                <div v-else-if="fieldType === 'radio'" class="radio-editor">
                    <label class="radio-group-label">{{ fieldLabel }}</label>
                    <div class="radio-options">
                        <label
                            v-for="option in fieldOptions"
                            :key="option.value || option"
                            class="mobile-radio"
                        >
                            <input
                                type="radio"
                                v-model="fieldValue"
                                :value="option.value || option"
                                :required="isRequired"
                                :disabled="readonly"
                            />
                            <span class="radio-circle"></span>
                            <span class="radio-text">{{ option.label || option }}</span>
                        </label>
                    </div>
                </div>

                <!-- Fallback Text Field -->
                <div v-else class="text-editor">
                    <label :for="fieldId">{{ fieldLabel }}</label>
                    <input
                        :id="fieldId"
                        type="text"
                        v-model="fieldValue"
                        class="mobile-input"
                        :placeholder="placeholder"
                        :required="isRequired"
                        :disabled="readonly"
                        ref="fallbackInput"
                    />
                </div>

                <!-- Context Info -->
                <div class="context-info" v-if="signeeType">
                    <i class="fa fa-user"></i>
                    <span>This field is assigned to: <strong>{{ formatSigneeType(signeeType) }}</strong></span>
                </div>

                <!-- Readonly Notice -->
                <div v-if="readonly && field && field.read_only" class="readonly-notice">
                    <i class="fa fa-lock"></i>
                    <span>This field is filled in automatically and cannot be edited.</span>
                </div>
                <div v-else-if="readonly" class="readonly-notice">
                    <i class="fa fa-info-circle"></i>
                    <span>This field is assigned to another role and cannot be edited by you.</span>
                </div>
            </div>

            <!-- Action Footer (hidden in landscape signature mode - actions are inline) -->
            <div class="editor-footer" v-if="!(fieldType === 'signature' && isLandscape)">
                <button
                    class="btn-primary-action"
                    @click="saveField"
                    :disabled="saving || !canSave"
                >
                    <i class="fa fa-check" v-if="!saving"></i>
                    <i class="fa fa-spinner fa-spin" v-else></i>
                    {{ saveButtonText }}
                </button>
                <button
                    v-if="canSkip"
                    class="btn-secondary-action"
                    @click="skipField"
                >
                    Skip for Now
                </button>
            </div>
        </div>
    </transition>
</template>

<script>
export default {
    name: 'MobileFieldEditor',
    props: {
        visible: {
            type: Boolean,
            default: false
        },
        field: {
            type: Object,
            default: null
        },
        currentStep: {
            type: Number,
            default: 1
        },
        totalSteps: {
            type: Number,
            default: 1
        },
        readonly: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            fieldValue: '',
            hasSignature: false,
            saving: false,
            canvasWidth: 300,
            canvasHeight: 200,
            isLandscape: false,
            signaturePadOptions: {
                dotSize: 2,
                minWidth: 1.5,
                maxWidth: 3.5,
                backgroundColor: 'rgba(255, 255, 255, 1)',
                penColor: 'rgba(0, 0, 0, 1)',
                velocityFilterWeight: 0.7,
                onBegin: () => {
                    this.hasSignature = true;
                }
            }
        };
    },
    computed: {
        fieldType() {
            if (!this.field) return 'text';
            const annotation = this.field.annotations?.[0];
            return annotation?.type || this.field.field_type || 'text';
        },

        fieldId() {
            return `mobile-field-${this.field?.id || 'input'}`;
        },

        fieldLabel() {
            return this.field?.field_label || this.field?.label || this.field?.field_name || this.field?.name || 'Field';
        },

        placeholder() {
            return this.field?.placeholder || `Enter ${this.fieldLabel.toLowerCase()}`;
        },

        signeeType() {
            const annotation = this.field?.annotations?.[0];
            return annotation?.signee_type || this.field?.assigned_to?.role;
        },

        fieldOptions() {
            return this.field?.options || this.field?.field_options || [];
        },

        isRequired() {
            return this.field?.required || false;
        },

        canSkip() {
            return !this.isRequired && !this.readonly;
        },

        canSave() {
            if (this.readonly) return false;
            if (this.fieldType === 'signature') {
                return this.hasSignature;
            }
            if (this.isRequired) {
                return this.fieldValue !== '' && this.fieldValue !== null && this.fieldValue !== undefined;
            }
            return true;
        },

        saveButtonText() {
            if (this.saving) return 'Saving...';
            if (this.currentStep < this.totalSteps) {
                return 'Save & Continue';
            }
            return 'Save';
        }
    },
    watch: {
        visible(newVal) {
            if (newVal) {
                this.onEditorOpen();
            } else {
                this.onEditorClose();
            }
        },
        field: {
            immediate: true,
            handler(newField) {
                if (newField) {
                    this.fieldValue = newField.value || '';
                }
            }
        }
    },
    mounted() {
        this.checkOrientation();
        this.calculateCanvasSize();
        window.addEventListener('resize', this.handleResize);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.handleResize);
        document.body.style.overflow = '';
    },
    methods: {
        onEditorOpen() {
            // Lock body scroll
            document.body.style.overflow = 'hidden';

            // Reset state
            this.saving = false;
            this.hasSignature = false;

            // Initialize value from field
            if (this.field) {
                this.fieldValue = this.field.value || '';
            }

            // Calculate canvas size for signature
            this.$nextTick(() => {
                this.calculateCanvasSize();

                // Clear signature pad if it exists
                if (this.$refs.signaturePad) {
                    this.$refs.signaturePad.clearSignature();
                }

                // Focus appropriate input
                this.focusInput();
            });
        },

        onEditorClose() {
            document.body.style.overflow = '';
        },

        checkOrientation() {
            this.isLandscape = window.innerWidth > window.innerHeight && window.innerHeight < 500;
        },

        handleResize() {
            this.checkOrientation();
            this.calculateCanvasSize();
        },

        calculateCanvasSize() {
            this.$nextTick(() => {
                if (this.isLandscape && this.fieldType === 'signature') {
                    // Landscape signature: maximize height, use ~1.5:1 aspect ratio
                    // Account for compact header (~50px) and margins
                    const availableHeight = window.innerHeight - 100;
                    this.canvasHeight = Math.max(availableHeight - 40, 150);
                    // In landscape, limit width to leave room for action buttons
                    const maxCanvasWidth = window.innerWidth - 200; // Reserve space for buttons
                    this.canvasWidth = Math.min(Math.floor(this.canvasHeight * 1.5), maxCanvasWidth);
                } else if (this.$refs.canvasWrapper) {
                    const wrapper = this.$refs.canvasWrapper;
                    const rect = wrapper.getBoundingClientRect();
                    // Full width minus padding, with aspect ratio for signature
                    this.canvasWidth = Math.floor(rect.width - 32);
                    this.canvasHeight = Math.min(Math.floor(this.canvasWidth * 0.5), 250);
                } else {
                    // Fallback dimensions
                    const vw = window.innerWidth;
                    this.canvasWidth = vw - 48;
                    this.canvasHeight = Math.min(Math.floor(this.canvasWidth * 0.5), 250);
                }
            });
        },

        focusInput() {
            const inputRefs = ['textInput', 'textareaInput', 'emailInput', 'phoneInput', 'numberInput', 'dateInput', 'selectInput', 'fallbackInput'];
            for (const refName of inputRefs) {
                if (this.$refs[refName]) {
                    this.$refs[refName].focus();
                    break;
                }
            }
        },

        handleBack() {
            this.$emit('back');
            this.$emit('close');
        },

        clearSignature() {
            if (this.$refs.signaturePad) {
                this.$refs.signaturePad.clearSignature();
                this.hasSignature = false;
            }
        },

        getSignatureData() {
            if (!this.$refs.signaturePad) return null;
            const { isEmpty, data } = this.$refs.signaturePad.saveSignature();
            return isEmpty ? null : data;
        },

        async saveField() {
            if (this.saving || !this.canSave) return;

            this.saving = true;

            try {
                let value;
                if (this.fieldType === 'signature') {
                    value = this.getSignatureData();
                    if (!value) {
                        this.saving = false;
                        return;
                    }
                } else {
                    value = this.fieldValue;
                }

                this.$emit('save', {
                    field: this.field,
                    value: value,
                    fieldType: this.fieldType
                });

            } catch (error) {
                console.error('Error saving field:', error);
                this.saving = false;
            }
        },

        skipField() {
            this.$emit('skip', this.field);
        },

        formatSigneeType(type) {
            if (!type) return 'Unassigned';
            const labels = {
                student: 'Student',
                guardian: 'Guardian',
                instructor: 'Instructor',
                staff: 'Staff',
                admin: 'Admin'
            };
            return labels[type] || type;
        }
    }
};
</script>

<style scoped>
.mobile-field-editor {
    position: fixed;
    top: 50px; /* Account for app header */
    left: 0;
    right: 0;
    bottom: 45px; /* Account for app footer */
    background-color: var(--ds-signing-bg, #F8FAFC);
    z-index: 10000;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

/* Header */
.editor-header {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    background-color: var(--ds-signing-card, #FFFFFF);
    border-bottom: 1px solid var(--ds-signing-border, #E2E8F0);
    flex-shrink: 0;
    min-height: 56px;
}

.back-btn {
    background: none;
    border: none;
    font-size: 20px;
    color: var(--ds-signing-primary, #2563EB);
    padding: 12px;
    min-width: 48px;
    min-height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    margin-left: -8px;
}

.editor-title {
    flex: 1;
    margin: 0;
    font-size: 17px;
    font-weight: 600;
    color: var(--ds-signing-text, #1E293B);
    text-align: center;
    padding: 0 8px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.clear-btn {
    background: none;
    border: none;
    font-size: 15px;
    color: var(--ds-signing-primary, #2563EB);
    padding: 12px 16px;
    min-height: 48px;
    cursor: pointer;
    font-weight: 500;
}

.clear-btn:disabled {
    color: var(--ds-signing-muted, #64748B);
    opacity: 0.5;
}

.header-spacer {
    width: 48px;
}

/* Step Indicator */
.step-indicator {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 16px;
    background-color: var(--ds-signing-card, #FFFFFF);
    border-bottom: 1px solid var(--ds-signing-border, #E2E8F0);
    flex-shrink: 0;
}

.step-text {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1px;
    color: var(--ds-signing-muted, #64748B);
    margin-bottom: 12px;
    text-transform: uppercase;
}

.step-dots {
    display: flex;
    align-items: center;
    gap: 8px;
}

.step-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    transition: all 0.2s ease;
}

.step-dot.completed {
    background-color: var(--ds-signing-success, #059669);
}

.step-dot.current {
    background-color: var(--ds-signing-primary, #2563EB);
    width: 12px;
    height: 12px;
    box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.2);
}

.step-dot.pending {
    background-color: var(--ds-signing-border, #E2E8F0);
}

/* Content */
.editor-content {
    flex: 1;
    overflow-y: auto;
    padding: 24px 16px;
    display: flex;
    flex-direction: column;
}

/* Signature Editor */
.signature-editor {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.canvas-wrapper {
    width: 100%;
    background-color: var(--ds-signing-card, #FFFFFF);
    border-radius: 12px;
    padding: 16px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.canvas-wrapper >>> canvas {
    width: 100% !important;
    border: 2px solid var(--ds-signing-border, #E2E8F0);
    border-radius: 8px;
    background-color: #FFFFFF;
    cursor: crosshair;
    touch-action: none;
}

.draw-hint {
    margin-top: 16px;
    font-size: 14px;
    color: var(--ds-signing-muted, #64748B);
    text-align: center;
}

.readonly-signature {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 150px;
    color: var(--ds-signing-muted, #64748B);
}

.readonly-signature i {
    font-size: 32px;
    margin-bottom: 12px;
    opacity: 0.5;
}

.readonly-signature p {
    margin: 0;
    font-size: 14px;
}

/* Form Editors */
.text-editor,
.textarea-editor,
.email-editor,
.phone-editor,
.number-editor,
.date-editor,
.select-editor {
    display: flex;
    flex-direction: column;
}

.text-editor label,
.textarea-editor label,
.email-editor label,
.phone-editor label,
.number-editor label,
.date-editor label,
.select-editor label,
.radio-group-label {
    font-size: 14px;
    font-weight: 600;
    color: var(--ds-signing-text, #1E293B);
    margin-bottom: 12px;
}

.mobile-input,
.mobile-textarea,
.mobile-select {
    width: 100%;
    padding: 16px;
    font-size: 16px;
    border: 2px solid var(--ds-signing-border, #E2E8F0);
    border-radius: 12px;
    background-color: var(--ds-signing-card, #FFFFFF);
    color: var(--ds-signing-text, #1E293B);
    transition: border-color 0.2s;
    -webkit-appearance: none;
    appearance: none;
}

.mobile-input:focus,
.mobile-textarea:focus,
.mobile-select:focus {
    outline: none;
    border-color: var(--ds-signing-primary, #2563EB);
}

.mobile-input:disabled,
.mobile-textarea:disabled,
.mobile-select:disabled {
    background-color: #F1F5F9;
    color: var(--ds-signing-muted, #64748B);
    cursor: not-allowed;
}

.mobile-textarea {
    min-height: 140px;
    resize: vertical;
}

.mobile-select {
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%2364748B' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
    background-position: right 16px center;
    background-repeat: no-repeat;
    background-size: 20px;
    padding-right: 48px;
}

/* Checkbox Editor */
.checkbox-editor {
    padding: 8px 0;
}

.mobile-checkbox {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px;
    background-color: var(--ds-signing-card, #FFFFFF);
    border-radius: 12px;
    cursor: pointer;
    min-height: 56px;
}

.mobile-checkbox input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
}

.checkbox-box {
    width: 28px;
    height: 28px;
    border: 2px solid var(--ds-signing-border, #E2E8F0);
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    flex-shrink: 0;
}

.mobile-checkbox input:checked + .checkbox-box {
    background-color: var(--ds-signing-primary, #2563EB);
    border-color: var(--ds-signing-primary, #2563EB);
}

.mobile-checkbox input:checked + .checkbox-box::after {
    content: '';
    width: 8px;
    height: 14px;
    border: solid white;
    border-width: 0 3px 3px 0;
    transform: rotate(45deg);
    margin-bottom: 4px;
}

.checkbox-text {
    font-size: 16px;
    color: var(--ds-signing-text, #1E293B);
}

/* Radio Editor */
.radio-editor {
    display: flex;
    flex-direction: column;
}

.radio-options {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.mobile-radio {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px;
    background-color: var(--ds-signing-card, #FFFFFF);
    border-radius: 12px;
    cursor: pointer;
    min-height: 56px;
}

.mobile-radio input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
}

.radio-circle {
    width: 28px;
    height: 28px;
    border: 2px solid var(--ds-signing-border, #E2E8F0);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    flex-shrink: 0;
}

.mobile-radio input:checked + .radio-circle {
    border-color: var(--ds-signing-primary, #2563EB);
}

.mobile-radio input:checked + .radio-circle::after {
    content: '';
    width: 14px;
    height: 14px;
    background-color: var(--ds-signing-primary, #2563EB);
    border-radius: 50%;
}

.radio-text {
    font-size: 16px;
    color: var(--ds-signing-text, #1E293B);
}

/* Context Info */
.context-info {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 24px;
    padding: 12px 16px;
    background-color: var(--ds-signing-card, #FFFFFF);
    border-radius: 8px;
    font-size: 14px;
    color: var(--ds-signing-muted, #64748B);
}

.context-info i {
    color: var(--ds-signing-primary, #2563EB);
}

/* Readonly Notice */
.readonly-notice {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 24px;
    padding: 16px;
    background-color: #FEF3C7;
    border: 1px solid #FCD34D;
    border-radius: 8px;
    font-size: 14px;
    color: #92400E;
}

.readonly-notice i {
    font-size: 18px;
    color: #F59E0B;
    flex-shrink: 0;
}

/* Footer */
.editor-footer {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    background-color: var(--ds-signing-card, #FFFFFF);
    border-top: 1px solid var(--ds-signing-border, #E2E8F0);
    flex-shrink: 0;
}

.btn-primary-action {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
    padding: 16px;
    font-size: 17px;
    font-weight: 600;
    color: #FFFFFF;
    background-color: var(--ds-signing-primary, #2563EB);
    border: none;
    border-radius: 12px;
    cursor: pointer;
    min-height: 56px;
    transition: background-color 0.2s;
}

.btn-primary-action:hover:not(:disabled) {
    background-color: #1D4ED8;
}

.btn-primary-action:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.btn-secondary-action {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 14px;
    font-size: 15px;
    font-weight: 500;
    color: var(--ds-signing-muted, #64748B);
    background-color: transparent;
    border: none;
    cursor: pointer;
    min-height: 48px;
}

.btn-secondary-action:hover {
    color: var(--ds-signing-text, #1E293B);
}

/* Transition */
.mobile-editor-enter-active,
.mobile-editor-leave-active {
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.mobile-editor-enter,
.mobile-editor-leave-to {
    transform: translateY(100%);
}

/* Landscape Signature Mode */
@media (orientation: landscape) and (max-height: 500px) {
    .mobile-field-editor {
        /* Account for horizontal notch safe areas */
        left: env(safe-area-inset-left, 0);
        right: env(safe-area-inset-right, 0);
        /* Remove top/bottom offsets in landscape - use full height */
        top: 0;
        bottom: 0;
        padding-left: env(safe-area-inset-left, 0);
        padding-right: env(safe-area-inset-right, 0);
    }

    .editor-header {
        padding: 8px 12px;
        min-height: 44px;
    }

    .editor-title {
        font-size: 15px;
    }

    .editor-content {
        padding: 12px 16px;
    }

    /* Landscape signature layout */
    .signature-editor.landscape {
        flex-direction: row;
        align-items: stretch;
        gap: 16px;
        flex: 1;
        height: 100%;
    }

    .signature-editor.landscape .canvas-wrapper {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 8px;
    }

    .signature-editor.landscape .landscape-actions {
        display: flex;
        flex-direction: column;
        justify-content: center;
        gap: 12px;
        min-width: 140px;
        padding: 8px;
    }

    .signature-editor.landscape .landscape-actions .btn-primary-action {
        padding: 12px 16px;
        min-height: 48px;
        font-size: 15px;
    }

    .signature-editor.landscape .landscape-actions .btn-secondary-action {
        padding: 10px 16px;
        min-height: 40px;
        font-size: 14px;
    }
}
</style>
